/**
 * The pool declarations, in a module with no browser in it.
 *
 * They lived in `store-setup.ts`, which is imported by a module that mounts a
 * page — so the guard over them could not import them and read the source with
 * a regex instead. @Codex refused that for the right reason:
 *
 * > A new or reformatted pool can fall outside the parser while the existing
 * > four pools and >20 definitions keep the non-vacuity floor green. **That
 * > recreates the exact "guard cannot distinguish what it names" failure.**
 *
 * Which it does, and worse: the guard was written *against* that failure. So
 * the data moved to where a test can hold the real objects, and
 * `apps/demo/test/relation-targets.test.ts` asserts the typed declarations
 * rather than their formatting.
 */
import type { Role, RoleKind } from '@morph/spec';

/**
 * What a reference may point AT.
 *
 * **A DECLARATION, never a discovery** — decision 0049's rule for `across`,
 * applied to the other half of a relation. The store holds every object and
 * could go looking for ones that "fit"; that would put the choice of what a
 * Filmmaker may be somewhere nobody wrote it down.
 *
 * Without this the attach picker offered whatever the current lens happened to
 * be showing, which for Films is other FILMS — so a person could say *"this
 * film's filmmaker is Chungking Express"*, and it persisted. Found by @Codex,
 * driving the deployed preview; the harness missed it because it chose an
 * arbitrary option and checked only that something landed and survived.
 */
export type Targets =
  /** A kind inside this pool's own manifest. Directors are `typeHint: 'person'`
   *  objects seeded beside the films that point at them. */
  | { readonly typeHint: string }
  /** Another pool, resolved to its scope. Only meaningful where the pool also
   *  declares that document in `across` — a lens may read what it POINTS AT,
   *  never what it can merely name. */
  | { readonly pool: string };

/** A property the demo seeds. `handle` is only for wiring the seed rows up. */
export interface Def {
  handle: string; label: string; kind: RoleKind; role: Role;
  /** Ref roles only. Absent means "objects of this pool's own kind", which is
   *  what a note citing a note wants and what the picker did unconditionally. */
  targets?: Targets;
  /** Enum roles only: the property's OPTIONS, in their declared order. A
   *  declaration, never a discovery (decision 0049's rule, applied to the
   *  third thing): Notion orders a board's columns by the status property's
   *  options, and the renderer's first-seen order is only right by the
   *  accident of the seed. A host that owns the declaration feeds the order;
   *  the renderer never sorts (a taste decision the spec did not ask for).
   *
   *  An option may also DECLARE ITS TONE — decision 0063. Order and category
   *  are independent facts: reordering a workflow's columns must not repaint
   *  them, so the tone travels with the option rather than being its index.
   *  A bare string keeps working and takes the derived tone. */
  options?: readonly Option[];
}

/**
 * An enum option: its value, and optionally which categorical colour slot it
 * occupies — decision 0063.
 *
 * The tone is a NUMBER, never a colour, for the reason `tone.ts` gives at
 * length: a colour here would be one look's decision stored on the data, and
 * a second look showing the same pool would wear the first look's palette.
 *
 * Declared, because a hash cannot promise distinctness. Measured on the Tasks
 * pool before this existed: `Open` and `In progress` both hash to slot 270 of
 * twelve, so two of four statuses would have shared a colour even once a look
 * read the hash. Four intended categories need four authored slots.
 */
export type Option = string | { readonly value: string; readonly tone: number };

/** An option's value, whichever form it was declared in. */
export const optionValue = (o: Option): string => (typeof o === 'string' ? o : o.value);

/** An option's declared tone, or nothing — in which case `toneOf` derives one
 *  from the value, which is stable but not guaranteed distinct. */
export const optionTone = (o: Option): number | undefined =>
  (typeof o === 'string' ? undefined : o.tone);

/** The declared half of a pool — everything true before anything is seeded.
 *  `DemoPool` in `store-setup.ts` adds what bootstrap fills in. */
export interface PoolDef {
  readonly id: string;
  readonly label: string;
  readonly note: string;
  readonly typeHint: string;
  readonly defs: readonly Def[];
  /** Pool ids whose objects this pool's references may resolve INTO. Ids
   *  rather than manifest refs, because a manifest ref is content-addressed
   *  and does not exist until the pool it belongs to has been seeded. */
  readonly across?: readonly string[];
  /** The pool's own ICON, an emoji — what a Notion page owns and shows in
   *  its head, its tab and its sidebar row (the fifteenth verdict's fourth
   *  item: a page-owned icon, not a generic document glyph). Absent, a look
   *  draws its generic page glyph. */
  readonly icon?: string;
  /** The pool's COVER as a doc would wear it — a CSS image (a gradient, a
   *  colour), the way Coda's page cover is the page's own choice. Absent, a
   *  look draws no cover band. */
  readonly cover?: string;
  /**
   * The pool's own categorical TONE — task 0178.
   *
   * A number, not a colour: the pool says WHICH of a look's categorical
   * slots it occupies, and each look maps its own palette across them. A
   * colour here would be one look's decision stored on the data, which is
   * the mistake 0173 is about; a position in the DOM would be the mistake
   * 0178 is about, and is what this replaces.
   *
   * Fibery's sidebar chose its space colours with
   * `:nth-of-type(6n+1)` through `(6n+6)`. Six pools and a cycle of six, so
   * every pool held a stable colour and nothing looked wrong — the defect
   * was invisible at exactly six, and bites on a seventh pool, on a hidden
   * or filtered row, on any reorder, and on `0058` composition where two
   * looks order the same rail differently and the same pool gets two
   * colours.
   *
   * Absent, the runtime derives one from the row's own identity, so a pool
   * that declares nothing still gets a stable colour rather than none.
   */
  readonly tone?: number;
}

export const POOL_DEFS: readonly PoolDef[] = [
  {
    id: 'films', tone: 0, label: 'Films', typeHint: 'film', icon: '🎬',
    note: 'Media-heavy and deliberately ragged — four have no score, three no country. The director is an object.',
    defs: [
      { handle: 'title', label: 'Title', kind: 'text', role: 'title' },
      { handle: 'date', label: 'Released', kind: 'date', role: 'date' },
      { handle: 'author', label: 'Director', kind: 'text', role: 'author' },
      { handle: 'duration', label: 'Runtime', kind: 'number', role: 'duration' },
      { handle: 'score', label: 'Rating', kind: 'score', role: 'score' },
      { handle: 'origin', label: 'Country', kind: 'enum', role: 'origin' },
      { handle: 'poster', label: 'Poster', kind: 'media', role: 'media.poster-frame' },
      // Directors are their own objects, seeded with `typeHint: 'person'` in
      // this same manifest — so a Filmmaker points at a person, and the picker
      // must offer people rather than the films it sits among.
      { handle: 'person', label: 'Filmmaker', kind: 'ref', role: 'relation.peer',
        targets: { typeHint: 'person' } },
    ],
  },
  {
    id: 'archive', tone: 1, label: 'Archive', typeHint: 'archive', icon: '🗄️',
    note: 'Public domain film and recordings. Mixed types on purpose — two have video and a '
      + 'poster, two have audio and neither. Every one has a transcript.',
    defs: [
      { handle: 'title', label: 'Title', kind: 'text', role: 'title' },
      { handle: 'date', label: 'Published', kind: 'date', role: 'date' },
      { handle: 'author', label: 'By', kind: 'text', role: 'author' },
      { handle: 'video', label: 'Film', kind: 'av', role: 'media.video' },
      { handle: 'audio', label: 'Recording', kind: 'av', role: 'media.audio' },
      { handle: 'poster', label: 'Frame', kind: 'media', role: 'media.poster-frame' },
      { handle: 'transcript', label: 'Transcript', kind: 'text', role: 'transcript' },
      // THE POOL'S ONE CONVENTION, and it was already true before it was
      // modelled: every row's media URL is `archive.org`, and the pool note has
      // said "Public domain film and recordings" from the start. The fact was
      // in the data and in the prose, and carried by no property.
      //
      // Task 0092 is open on whether the demo may gain a convention so the
      // template rule becomes observable. The test it set is whether a person
      // would plausibly have this field — and an archive recording where its
      // material came from is what an archive IS, single-valued exactly when
      // the collection has one source. Not added to Tasks, whose 6/3/2/1 and
      // 4/3/3/2 splits are the evidence that ruled out seeding a modal value.
      { handle: 'source', label: 'Source', kind: 'enum', role: 'origin' },
    ],
  },
  {
    id: 'notes', tone: 2, label: 'Notes', typeHint: 'note', icon: '📝',
    note: 'No media at all — text, numbers, and notes that cite each other. A vault.',
    defs: [
      { handle: 'title', label: 'Title', kind: 'text', role: 'title' },
      { handle: 'body', label: 'Body', kind: 'text', role: 'body' },
      { handle: 'author', label: 'Author', kind: 'text', role: 'author' },
      { handle: 'date', label: 'Written', kind: 'date', role: 'date' },
      { handle: 'quantity', label: 'Words', kind: 'number', role: 'quantity' },
      // The note this one builds on. One ref slot holds one target, so the
      // SECOND and later citations are stored as edges instead — see
      // NOTE_LINKS.
      //
      // This comment used to end *"the slot is what makes `relation.peer`
      // appear in `deriveShape` ... edges alone are invisible to it"*, and
      // that was true and is not any more: `contributionOf` reads edge-backed
      // roles now, so a pool whose only relations are edges derives the role
      // too. Decision 0016 required that before relation mutations could be
      // enabled, and called the state this replaces "not an acceptable
      // compatibility state".
      //
      // The split stays, because it is a fact about storage rather than a
      // workaround: a ref slot holds exactly one target and these notes cite
      // more than one.
      // A note cites a note: the same kind, in the same manifest. Written down
      // rather than left to the default, because "the picker happened to be
      // showing the right thing" is how the Films picker went wrong.
      { handle: 'link', label: 'Links to', kind: 'ref', role: 'relation.peer',
        targets: { typeHint: 'note' } },
      /**
       * The work a note came out of, in ANOTHER document — and the reason it
       * exists is as much acceptance as product.
       *
       * @Codex ruled it, answering a question put to it directly: *"Declare a
       * second relation on Notes (yes), or add another browser-reachable
       * fixture with that shape."* Two things were unprovable without it.
       *
       * A pool declaring ONE relation draws no Connections stack, so it draws
       * no Open control either — which meant task 0078's acceptance case, two
       * occurrences of one slot activated through the host, was unreachable in
       * the product. A guard that can only assert attribute shape passes a host
       * that reads the token and opens the wrong thing.
       *
       * And its DOMAIN is deliberately not notes. @Codex: *"both the stage's
       * `relation.peer` declaration and the pane's `relation.peer` declaration
       * target Notes … reverting the picker to the stage owner can still
       * satisfy this check."* Two pools whose pickers offer the same set cannot
       * distinguish which pool the picker asked. Tasks is a different set.
       *
       * Semantically it is the mirror of the tasks pool's `Background`: work
       * points at the note behind it, and the note points back at the work it
       * came out of.
       */
      { handle: 'raised', label: 'Raised by', kind: 'ref', role: 'relation.parent',
        targets: { pool: 'tasks' } },
      /**
       * THE ROLE WHOSE DOMAIN DIFFERS FROM THE TASKS POOL'S.
       *
       * @Codex, twice: *"both the stage's `relation.peer` declaration and the
       * pane's `relation.peer` declaration target Notes … reverting the picker
       * to the stage owner can still satisfy this check … Yes—give the
       * exercised pane role a target domain different from the stage role."*
       *
       * The problem was that no shared role distinguished the two pools:
       * `relation.peer` resolved to notes from both, `relation.parent` to tasks
       * from both. While that held, NO browser assertion could tell which pool
       * a picker had asked, because both answers were the same set — so the
       * scoping repair in #121 was unfalsifiable rather than merely untested.
       *
       * `relation.child` is the pair that differs: **notes → notes, tasks →
       * tasks.** Exercising this row in a pane means a picker offering tasks is
       * visibly the wrong pool's answer.
       *
       * A follow-up note in a vault is also a real thing, which matters: a
       * declaration that exists only to be measured is scaffolding, and @Codex
       * refused exactly that once already on the tasks pool's third relation.
       */
      { handle: 'followup', label: 'Follow-ups', kind: 'ref', role: 'relation.child',
        targets: { typeHint: 'note' } },
    ],
    // Notes may now FOLLOW a reference into tasks, so it declares that document
    // — decision 0049: a lens may read what it POINTS AT, never what it can
    // merely name. Not 'notes': that is its own reading manifest.
    across: ['tasks'],
  },
  /**
   * THE FIBERY WORKSPACE — task 0150, phase 1. Two pools, because a Fibery
   * "database" maps onto what this demo calls a document, and the canon
   * clause is *bi-directional links between DATABASES*: the tasks↔notes pair
   * set the precedent (each side DECLARES its ref into the other; decision
   * 0049 keeps `across` narrow), and features↔sprints follows it with the
   * parent/child pair — a feature's parent is its sprint, a sprint's children
   * are its features.
   *
   * People are typed entities INSIDE the features manifest (`typeHint:
   * 'person'`), the way directors live beside films — entity types as
   * typeHints is the raw material 0150 names, and the AUTHORING clause (a
   * person defines a type end-to-end) is what will prove it a model; this
   * seed is deliberately not that proof.
   *
   * Feature statuses are ORDERED on purpose — Backlog → Building → Review →
   * Shipped — because the process-view clause (a state flow with legal
   * transitions and history) will need an order to constrain against. The
   * board can group by it today; whether the flow is a constraint on the
   * board or a new capability view is the 0150 question that goes to @Codex
   * before code.
   */
  {
    id: 'features', tone: 3, label: 'Features', typeHint: 'feature', icon: '🚀',
    note: 'A product team\'s feature database: a seeded status awaiting its process rules '
      + '(decision 0052 — not implemented yet), effort, an assignee who is a person '
      + 'object, and a sprint in ANOTHER database. Ragged on purpose.',
    defs: [
      { handle: 'title', label: 'Feature', kind: 'text', role: 'title' },
      // Four stages, four DECLARED tones — decision 0063. The numbers are
      // slots, not colours: each look maps its own palette across them.
      { handle: 'status', label: 'Stage', kind: 'enum', role: 'status',
        options: [
          { value: 'Backlog', tone: 4 }, { value: 'Building', tone: 1 },
          { value: 'Review', tone: 3 }, { value: 'Shipped', tone: 2 },
        ] },
      { handle: 'effort', label: 'Effort', kind: 'number', role: 'quantity' },
      { handle: 'due', label: 'Due', kind: 'date', role: 'date' },
      // The assignee is an ENTITY, not a string — the difference between a
      // Fibery field and a Notion text column, and the reason people are
      // seeded as objects in this manifest.
      { handle: 'assignee', label: 'Assignee', kind: 'ref', role: 'relation.peer',
        targets: { typeHint: 'person' } },
      // The cross-database half. A feature's parent is its sprint; the
      // sprints pool declares the inverse as `relation.child`. Both sides
      // are written when the seed wires one link, which is what makes the
      // bidirectional clause measurable in both directions over one edge.
      { handle: 'sprint', label: 'Sprint', kind: 'ref', role: 'relation.parent',
        targets: { pool: 'sprints' } },
    ],
    across: ['sprints'],
  },
  {
    id: 'sprints', tone: 4, label: 'Sprints', typeHint: 'sprint', icon: '🏃',
    note: 'The other Fibery database: time-boxed sprints with a lead, a goal, and the '
      + 'features they carry — the inverse side of the features link.',
    defs: [
      { handle: 'title', label: 'Sprint', kind: 'text', role: 'title' },
      { handle: 'goal', label: 'Goal', kind: 'text', role: 'body' },
      { handle: 'author', label: 'Lead', kind: 'text', role: 'author' },
      // A sprint is a RANGE — the two-role date pair the timeline reads.
      { handle: 'start', label: 'Starts', kind: 'date', role: 'date.start' },
      { handle: 'end', label: 'Ends', kind: 'date', role: 'date.end' },
      // The inverse of features' `Sprint`. One slot holds the first feature;
      // the rest are edges, exactly the notes-citation storage split.
      { handle: 'feature', label: 'Features', kind: 'ref', role: 'relation.child',
        targets: { pool: 'features' } },
    ],
    across: ['features'],
  },
  {
    id: 'tasks', tone: 5, label: 'Tasks', typeHint: 'task', icon: '☑️',
    cover: 'linear-gradient(120deg, #1d2a3a 0%, #3b4d63 55%, #8b8f7a 100%)',
    note: 'Carries a four-value status — low enough cardinality to group by.',
    defs: [
      { handle: 'title', label: 'Title', kind: 'text', role: 'title' },
      // The options in PROCESS order — Notion's To-do → In progress → In
      // review → Complete — which is the order a board's columns take.
      { handle: 'status', label: 'Status', kind: 'enum', role: 'status',
        options: [
          { value: 'Open', tone: 4 }, { value: 'In progress', tone: 1 },
          { value: 'In review', tone: 3 }, { value: 'Done', tone: 2 },
        ] },
      // The SECOND groupable role, which is what makes a sub-grouped board
      // reachable — `origin` and `status` are the only enum roles the
      // vocabulary has, and no pool carried both. See `TASK_ROWS`.
      { handle: 'area', label: 'Area', kind: 'enum', role: 'origin' },
      { handle: 'author', label: 'Owner', kind: 'text', role: 'author' },
      { handle: 'date', label: 'Updated', kind: 'date', role: 'date' },
      { handle: 'start', label: 'Started', kind: 'date', role: 'date.start' },
      { handle: 'end', label: 'Finished', kind: 'date', role: 'date.end' },
      // The note this task is about, which lives in ANOTHER document. An
      // ordinary `relation.peer` ref: the graph, the backlinks panel and
      // `node-slot` all bind it without knowing it crosses anything.
      { handle: 'note', label: 'Background', kind: 'ref', role: 'relation.peer',
        targets: { pool: 'notes' } },
      { handle: 'parent', label: 'Parent task', kind: 'ref', role: 'relation.parent',
        targets: { pool: 'tasks' } },
      // The third declared relation, and @Codex ruled it should be a real
      // capability rather than acceptance-only scaffolding: *"this is a genuine
      // task-manager capability … it also exercises the exact product surface
      // whose two-role behavior is already under test."*
      //
      // It exists because the 0075 ruling makes the THREE-role visual the
      // deciding case for whether the Connections stack is compact enough, and
      // a stack nobody can open is a stack nobody can judge. Unit-rendered HTML
      // proving three controls is not that inspection.
      { handle: 'child', label: 'Subtasks', kind: 'ref', role: 'relation.child',
        targets: { pool: 'tasks' } },
    ],
    // NOT 'tasks'. `Parent task` points at another task, which is the SAME
    // document — the tasks lens already reads the tasks manifest, because that
    // is its reading manifest. `across` declares which OTHER documents a lens
    // may follow a reference into, and decision 0049 keeps it deliberately
    // narrow: *"a lens may read what it POINTS AT, never what it can merely
    // name."* Naming your own document there is redundant at best and a widened
    // boundary declaration at worst.
    //
    // @Codex sent two reconstructions of this change and they differed here.
    // The second is right.
    across: ['notes'],
  },
];
